// react
import { useRef, useState, MouseEvent } from 'react'

// external libs
import { Add, EditOutlined } from '@material-ui/icons'
import { Box, IconButton, Tooltip, Typography } from '@material-ui/core'
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays'
import { useForm } from 'react-final-form'

// components
import AdditionalDocField from './AdditionalDocField'
import MyAccordionCtrl from '../../../../control/accordion/accordion'
import MyButton from '../../../../control/button/button'
import Dialog from '../../../../control/dialog/dialog'

// hooks
import useTranslate from '../../../../i18n/useTranslate'
import useManageProjectStore from '../../../../stores/manageProject/useManageProjectStore'
import useLoading from '../../../../control/loading/useLoading'
import useWebService from '../../../../services/web/useWebService'

// types
import type { DialogType } from '../../../../control/dialog/dialog'
import type { GetProjectResultType as Project } from '../../../../services/web/types/responseWebServiceTypes'
import type {
    DocumentInfo,
    UpdateFileProjectRequestType as UpdateFileProjectRequest,
} from '../../../../services/web/types/requestWebServiceTypes'

import { trimEmplyFileInfo, withFileTokenInfo } from '../../../../utilities/additionalDoc'
import { UploadFileObjType } from '../../project/page/closeProjectResearcher/closeProjectResearcherType'
import useFolderAndTab from '../../project/useFolderAndTab'

export interface UploadAdditionalDocProps {
    name?: string
    isViewMode?: boolean
    isEditable?: boolean
}

const UploadAdditionalDoc = (props: UploadAdditionalDocProps) => {
    const { name = 'documentAdditionalInfo', isViewMode, isEditable } = props
    const form = useForm()
    const translate = useTranslate()
    const loadingHook = useLoading()
    const project = useManageProjectStore().projectData
    const initialValues = project?.[name as keyof Project] ?? []
    const webServices = useWebService()
    const dialogRef = useRef<Record<string, Function>>()
    const [dialogInfo, setDialogInfo] = useState<Partial<DialogType>>()
    const [isEditting, setIsEditting] = useState<boolean>(false)
    const tabAndFolderHook = useFolderAndTab()

    const handleAdd = (fields: FieldArrayRenderProps<any, HTMLElement>['fields']) => {
        fields.push({ fileInfo: [], topicId: null, topicName: '' })
    }

    const handleDelete = (fields: FieldArrayRenderProps<any, HTMLElement>['fields'], index: number) => {
        const hasData = !!fields.value[index].topicName || fields.value[index].fileInfo.length > 0

        if (hasData) {
            setDialogInfo({
                title: 'ลบรายการเอกสารเพิ่มเติม',
                content: 'ท่านต้องการลบเอกสารเพิ่มเติม ใช่หรือไม่?',
                type: 'alert-confirm',
                onClickConfirm: () => {
                    dialogRef.current?.closeDialog()
                    fields.remove(index)
                },
            })

            dialogRef.current?.openDialog()
        } else {
            fields.remove(index)
        }
    }

    const handleEdit = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        setIsEditting(true)
    }

    const handleCancelEdit = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        form.change(name, initialValues)
        setIsEditting(false)
    }

    const getDocumentInfo = (val: any) => {
        // * This function is old logic copy from other getDocumentInfo function
        let fileDataList = val.uploadFile.filter((itm: any) => itm.uploadFileObj.fileList.length > 0)
        let myFileData: Array<DocumentInfo> = []

        fileDataList.forEach((uploadObj: UploadFileObjType) => {
            const subTopicLst = uploadObj?.uploadFileObj.subTopicPageList?.map((subtopic) => {
                return {
                    subtopicID: subtopic.subtopicID,
                    page: subtopic.page,
                }
            })

            const fileTokenInfo: Array<{
                replaceFileID: number
                fileToken: string
                fileExtension?: string
                fileID: any
            }> =
                uploadObj.uploadFileObj.fileList?.map((file) => {
                    return {
                        //@ts-ignore
                        replaceFileID: file?.replaceFileID ? file.replaceFileID : null,
                        fileToken: file.fileToken,
                        fileExtension: file.filename.split('.').pop(),
                        fileID: file?.fileID ? file.fileID : null,
                    }
                }) ?? []

            myFileData.push({
                documentTopicID: uploadObj?.uploadFileObj?.documentTopicID,
                fileTokenInfo: fileTokenInfo,
                subtopic: subTopicLst && subTopicLst.length > 0 ? subTopicLst : [],
            })
        })
        return myFileData
    }

    const handleSubmitEdit = async (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()

        try {
            loadingHook.loadingOpen()
            const values = form.getState().values

            const params: UpdateFileProjectRequest = {
                projectID: project?.projectID ?? null,
                submittedID: project?.submittedID ?? null,
                documentInfo: getDocumentInfo(values),
                documentAdditionalInfo: withFileTokenInfo(trimEmplyFileInfo(values.documentAdditionalInfo)),
            }
            await webServices.updateFileProject(params)
            await tabAndFolderHook.reloadProjectData(project?.projectID, project?.submittedID)
            setIsEditting(false)
        } catch (error) {
            console.error(error)
        } finally {
            loadingHook.loadingClose()
        }
    }

    return (
        <>
            <FieldArray name={name}>
                {({ fields }) => (
                    <MyAccordionCtrl
                        renderTitle={
                            <Box display="flex" width="100%" alignItems="center" justifyContent="space-betweeen">
                                <Box flexGrow={1}>
                                    <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                                        {translate.get('LABEL.UPLOAD_ADDITIONAL_DOC')}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                    {isEditable && !isEditting ? (
                                        <Tooltip title={translate.get('BUTTON.EDIT')}>
                                            <IconButton size="small" onClick={handleEdit}>
                                                <EditOutlined color="primary" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}

                                    {isEditting ? (
                                        <>
                                            <MyButton variant="contained" color="secondary" onClick={handleCancelEdit}>
                                                {translate.get('BUTTON.CANCEL')}
                                            </MyButton>
                                            <MyButton variant="contained" color="primary" onClick={handleSubmitEdit}>
                                                {translate.get('BUTTON.CONFIRM')}
                                            </MyButton>
                                        </>
                                    ) : null}
                                </Box>
                            </Box>
                        }
                    >
                        <Box display="flex" flexDirection="column" width="100%">
                            <Box style={{ alignSelf: 'flex-end' }}>
                                {!isViewMode || isEditting ? (
                                    <MyButton
                                        size="large"
                                        variant="contained"
                                        color="primary"
                                        startIcon={<Add />}
                                        style={{
                                            marginBottom: 32,
                                        }}
                                        onClick={() => handleAdd(fields)}
                                    >
                                        เพิ่มรายการเอกสาร
                                    </MyButton>
                                ) : null}
                            </Box>
                            <Box display="flex" flexDirection="column" width="100%">
                                {fields.map((name, index) => (
                                    <AdditionalDocField
                                        key={name}
                                        no={index + 1}
                                        name={name}
                                        isEditable={isEditable}
                                        disabled={isViewMode && !isEditting}
                                        onDelete={() => handleDelete(fields, index)}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </MyAccordionCtrl>
                )}
            </FieldArray>

            <Dialog onRef={(funcs) => (dialogRef.current = funcs)} {...dialogInfo} />
        </>
    )
}

export default UploadAdditionalDoc
